import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import sample from "../../../../assets/sample.pdf";
import IMG_Info from "../../../../assets/images/icon/info.png";
import {
  spotConstants,
  translationConstants,
  globalGridActions,
  availableServicesConstants,
  StorageKeys,
  roles,
} from "../../../../constants";
import { ToastifyService } from "../../../../services";
import { getLocalStorageItem, getSportObjByName } from "../../../../system";
import PrivateSpotModal from "./PrivateSpotModal";
import cancelIcon from "../../../../assets/images/icon/cancelIcon.svg";
import tickIcon from "../../../../assets/images/icon/tickIcon.svg";
import CustomModal from "../../../../shared/components/CustomModal/CustomModal";
import FileUploader from "../../../../shared/components/UploadFile/FileUploader";
import InputFieldWrapper from "../../../../shared/components/InputFieldWrapper/InputFieldWrapper";

const { PRIVATE, PUBLIC } = spotConstants.spotType;

const AddLocationModal = (props) => {
  const methods = useForm();
  const { t } = useTranslation();

  const isAdmin = getLocalStorageItem(StorageKeys.activeRole) === roles.SuperAdmin || getLocalStorageItem(StorageKeys.activeRole) === roles.Admin;


  const formState = useSelector((state) => state.centerReducer.formState);
  const centerProfileData = useSelector(
    (state) => state.centersReducer.centerProfileData
  );
  const publicSports = useSelector((state) => state.SpotsReducer.publicSpots);
  const privateSpotData = useSelector(
    (state) => state.SpotsReducer?.spotDataById
  );

  const [spotType, setSpotType] = useState(
    props.modalData?.spotTypeId || PUBLIC
  );

  const getPrivateSpotName = () =>
    props.isAdmin || props.isCenterUser || props.isGuestUser
      ? props.modalData?.spotName
      : props.modalData?.spotInfo?.spotInfo?.name;

  const [privateSpotName, setPrivateSpotName] = useState(getPrivateSpotName());
  const [disabled, setDisabled] = useState(false);
  const [allowedSports, setAllowedSports] = useState([]);
  const [privateModal, setPrivateModal] = useState(false);
  const [safetyDocsMessage, setSafetyDocsMessage] = useState("");
  const [operationalDocsMessage, setOperationalDocsMessage] = useState("");

  const {
    setValue,
    trigger,
    getValues,
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const handleSpotTypeChange = (e) => {
    var sportType = +e.currentTarget?.value;
    setSpotType(sportType);
    if (sportType === PRIVATE && !getValues("spotInfo")) {
      togglePrivateSpotModal();
    }
  };

  const onClosePrivateModal = (flag) => {
    if (!flag && !getValues("spotInfo")) {
      setSpotType(PUBLIC);
      setValue("spotType", `${PUBLIC}`);
    }
    togglePrivateSpotModal(flag);
  };

  const togglePrivateSpotModal = (flag) => {
    if (flag === true || flag === false) {
      setPrivateModal(flag);
    } else setPrivateModal((prev) => !prev);
  };

  const onSubmitPrivateSpotModal = (data) => {
    setValue("spotInfo", data);
    setPrivateSpotName(data.spotInfo.name);
    togglePrivateSpotModal(false);
  };

  const onSubmit = (dataToSend) => {
    if (
      !allowedSports.length > 0 ||
      !dataToSend.sportData.sports ||
      !dataToSend.sportData.sports.length > 0
    ) {
      ToastifyService.showError(translationConstants.ATLEAST_ONE_SPORT);
      return;
    }

    const selectedLat =
      spotType === PRIVATE
        ? dataToSend.spotInfo?.spotInfo?.lat
        : publicSports.find((x) => x.id === dataToSend.publicSpotId)?.lat;
    const selectedLong =
      spotType === PRIVATE
        ? dataToSend.spotInfo?.spotInfo?.long
        : publicSports.find((x) => x.id === dataToSend.publicSpotId)?.long;

    const match = props?.existingSpotCoordinates?.some(
      (obj) =>
        obj.lat === selectedLat &&
        obj.long === selectedLong &&
        (props.modalData?.rowIndex === null ||
          obj.rowIndex !== props.modalData?.rowIndex)
    );

    if (match) {
      ToastifyService.showError(
        translationConstants.SPOT_COORDINATES_ALREADY_EXISTS
      );
      return;
    }

    const obj = {
      spotTypeId: spotType,
      services: dataToSend.sportData.services,
      spotInfo:
        spotType === PRIVATE
          ? {
              spotInfo: dataToSend.spotInfo.spotInfo,
              sportIds: dataToSend?.spotInfo?.spotInfo?.sports,
            }
          : null,
      safetyDocuments: dataToSend.sportData.safetyDocuments,
      id: spotType === PUBLIC ? dataToSend.publicSpotId : null,
      spotName:
        spotType === PUBLIC
          ? publicSports.find((x) => x.id === dataToSend.publicSpotId)?.name
          : dataToSend.spotInfo?.spotInfo?.name,
      centerSpotSports: dataToSend.sportData.sports,
      operationalDocuments: dataToSend.sportData.operationalDocuments,
      rowIndex: props.modalData?.rowIndex,

      publicSpotLat:
        spotType === PUBLIC &&
        publicSports.find((x) => x.id === dataToSend.publicSpotId)?.lat,
      publicSpotLng:
        spotType === PUBLIC &&
        publicSports.find((x) => x.id === dataToSend.publicSpotId)?.long,
    };

    props.onSubmit(obj);
  };

  // validation, at least one option checkbox needs to be ticked
  const atLeastOneOptionChecked = (option) => option.length > 0;

  const handleFileUpload = useCallback(
    (file, key) => {
      setValue(key, file, { shouldValidate: true });
    },
    [setValue]
  );

  // Function to filter out sports based on selected sportIds
  const getSelectedSports = (selectedSpot) => {
    let centerSports =
      props.isAdmin || props.isCenterUser
        ? centerProfileData?.sportIds
        : formState?.sports;

    return (
      centerSports
        ?.filter(
          // Check if the sport is included in selectedSpot either as a string or a number
          (sport) =>
            selectedSpot.includes(+sport) || selectedSpot.includes(`${sport}`)
        )
        // Map each selected sport to its object
        .map((sport) => getSportObjByName(+sport))
    );
  };

  useEffect(() => {
    if (props.modalData) {
      setValue("spotType", `${props.modalData.spotTypeId}`);
      setSpotType(+props.modalData.spotTypeId || PUBLIC);
      setValue("publicSpotId", props.modalData.id);
      if (`${spotType}` === `${PUBLIC}`)
        setValue("sportData.sports", props.modalData.centerSpotSports);
      else if (`${spotType}` === `${PRIVATE}`) {
        if (props.isAdmin || props.isCenterUser)
          setValue("sportData.sports", props.modalData?.centerSpotSports);
        else setValue("sportData.sports", props.modalData?.centerSpotSports);
      }
      setValue("sportData.safetyDocuments", props.modalData.safetyDocuments);
      setValue(
        "sportData.operationalDocuments",
        props.modalData.operationalDocuments
      );
      setDisabled(props.modalData.disabled);
      setValue("sportData.services", props.modalData.services);
      setValue("spotInfo", props.modalData.spotInfo);
    }
    // eslint-disable-next-line
  }, [props.modalData, getValues("sportData.sports")]);

  useEffect(() => {
    // Define an array to store selected sports
    let sports = [];
    // Extract selected sports based on spotType
    // eslint-disable-next-line

    if (`${spotType}` === `${PUBLIC}`) {
      const spotId = getValues("publicSpotId");
      if (spotId) {
        // Find the public spot by id and extract its sportIds
        const selectedSpot =
          publicSports.find((x) => x.id === spotId)?.sportIds || [];
        sports = getSelectedSports(selectedSpot);
      }
      // eslint-disable-next-line
    } else if (`${spotType}` === `${PRIVATE}`) {
      // Extract sportIds from private spot info
      const privateSports =
        getValues("spotInfo")?.spotInfo?.sports || props.modalData?.sportIds;
      const selectedSpot = privateSports || [];
      sports = getSelectedSports(selectedSpot);
    }

    // Set the allowed sports state
    setAllowedSports(sports);
    // eslint-disable-next-line
  }, [formState?.sports, getValues("publicSpotId"), spotType, privateModal]);

  useEffect(() => {
    if (props.isCenterUser && props.currentAction !== globalGridActions.ADD) {
      privateSpotData.spotInfo = {
        ...privateSpotData.spotInfo,
        sports: privateSpotData?.sportIds?.map((id) => id.toString()),
      };
      setValue("spotInfo", privateSpotData);
    }
    // eslint-disable-next-line
  }, [privateSpotData]);

  return (
    <>
      <FormProvider {...methods}>
        <Form
          noValidate
          className="RegisterationDataForm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="ViewHeader">
            <p className="viewUserPara">
              {props.modalData?.disabled
                ? t(translationConstants.VIEW_OPERATING_LOCATION)
                : typeof props.modalData?.rowIndex === "number"
                ? t(translationConstants.EDIT_OPERATING_LOCATION)
                : t(translationConstants.ADD_OPERATING_LOCATION)}
            </p>
          </div>
          <p className="horizentalLine" />
          <div className="m-t-24"></div>

          <Container>
            <Row>
              <Col>
                <p className="text-dark font-weight-700">
                  {t(translationConstants.OPERATING_LOCATION)}:
                </p>
                {!isAdmin && (
                  <span className="text-green">
                    {t(translationConstants.IS_LOCATION_PUBLIC_PRIVATE)}
                  </span>
                )}
              </Col>
            </Row>
          </Container>
          <Container>
            <Row>
              <Col md={12} className=" mb-3">
                <Form.Group>
                  <div className="d-flex radio-button">
                    <Form.Check
                      inline
                      type="radio"
                      value={PUBLIC}
                      disabled={disabled}
                      id="inline-radio-1"
                      label={t(translationConstants.PUBLIC_SPOT)}
                      defaultChecked={spotType === PUBLIC}
                      {...register("spotType", {
                        required: t(translationConstants.SPOT_TYPE_REQUIRED),
                      })}
                      onChange={handleSpotTypeChange}
                      className={
                        props.isCenterSpotUpdate
                          ? "cursor-not-allowed pointer-events-none"
                          : ""
                      }
                    />
                    <Form.Check
                      inline
                      label={t(translationConstants.PRIVATE_SPOT)}
                      value={PRIVATE}
                      disabled={disabled}
                      type="radio"
                      id="inline-radio-2"
                      defaultChecked={spotType === PRIVATE}
                      {...register("spotType", {
                        required: t(translationConstants.SPOT_TYPE_REQUIRED),
                      })}
                      onChange={handleSpotTypeChange}
                      className={
                        props.isCenterSpotUpdate
                          ? "cursor-not-allowed pointer-events-none"
                          : ""
                      }
                    />
                  </div>
                  {errors.spotType && (
                    <p className="errorText">{errors.spotType.message}</p>
                  )}
                </Form.Group>
              </Col>

              {spotType === PUBLIC ? (
                <InputFieldWrapper
                  label={`${t(translationConstants.SELECT_PUBLIC_SPOT)}:*`}
                >
                  <Form.Select
                    id="publicSpotId"
                    disabled={disabled || props.isCenterSpotUpdate}
                    {...register("publicSpotId", {
                      required: t(translationConstants.SPOT_REQUIRED),
                    })}
                    onChange={(e) => {
                      const value = e.target.value;
                      setValue("publicSpotId", value, {
                        shouldValidate: true,
                      });

                      trigger("publicSpotId");
                      e.stopPropagation();
                    }}
                  >
                    <option value="">
                      {t(translationConstants.SELECT_SPOT)}
                    </option>
                    {publicSports?.map((spot) => (
                      <option key={spot.id} value={spot.id}>
                        {spot.name}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.publicSpotId && (
                    <p className="errorText">{errors.publicSpotId.message}</p>
                  )}
                </InputFieldWrapper>
              ) : spotType === PRIVATE ? (
                <Col md={12}>
                  <p className="d-flex align-items-center font-weight-700 p-0">
                    {privateSpotName}
                    <span
                      className={`mx-4 mt-2 ${
                        disabled
                          ? "cursor-not-allowed d-none pointer-events-none"
                          : ""
                      }`}
                      onClick={() => {
                        if (!disabled) {
                          togglePrivateSpotModal(true);
                        }
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="15"
                        viewBox="0 0 30 30"
                        fill="none"
                        className={`${disabled ? "cursor-not-allowed" : ""}`}
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M26.5833 24.167V15.7087H24.1667V24.167H4.83332V4.83366H13.2917V2.41699H4.83332C3.49863 2.41699 2.41666 3.49897 2.41666 4.83366V24.167C2.41666 25.5017 3.49863 26.5837 4.83332 26.5837H24.1667C25.5013 26.5837 26.5833 25.5017 26.5833 24.167ZM23.1735 3.29113C22.6197 2.73287 21.8613 2.41699 21.0701 2.41699C20.2799 2.41699 19.5223 2.73216 18.9653 3.29258L8.85284 13.4049C7.94921 14.2011 7.35685 15.3858 7.25391 16.66L7.24999 20.5448V21.7531H12.246C13.6166 21.6591 14.813 21.061 15.657 20.0874L25.7121 10.0365C26.27 9.47869 26.5834 8.72206 26.5834 7.93313C26.5834 7.1442 26.27 6.38757 25.7121 5.82971L23.1735 3.29113ZM12.1606 19.3395C12.8062 19.2938 13.407 18.9934 13.8895 18.4415L21.2143 11.1167L17.8862 7.78836L10.5078 15.1644C10.0182 15.5974 9.71536 16.2031 9.66666 16.7573V19.3372L12.1606 19.3395ZM19.5954 6.0798L22.9232 9.40782L24.0033 8.3277C24.1079 8.22305 24.1667 8.08112 24.1667 7.93313C24.1667 7.78514 24.1079 7.6432 24.0033 7.53856L21.4613 4.99661C21.3579 4.89232 21.217 4.83366 21.0701 4.83366C20.9232 4.83366 20.7824 4.89232 20.6789 4.99661L19.5954 6.0798Z"
                          fill="#007F58"
                        />
                      </svg>
                    </span>
                  </p>
                </Col>
              ) : (
                ""
              )}

              <Col md={9} sm={12} className="mb-3">
                <Form.Label className="Form-labels">
                  {`${t(translationConstants.CHOOSE_SPORTS_YOU_OFFER)}:*`}
                </Form.Label>
                <div className="d-flex flex-wrap justify-content-between col-md-12 px-0">
                  {allowedSports?.length > 0
                    ? allowedSports.map((sport) => (
                        <Form.Group
                          controlId={sport.value}
                          className="d-flex flex-column justify-content-space-between"
                        >
                          <Form.Check
                            className="offer"
                            type="checkbox"
                            disabled={disabled}
                            value={sport.value}
                            label={<p>{t(sport.label)} </p>}
                            {...register("sportData.sports", {
                              validate: atLeastOneOptionChecked,
                            })}
                          />
                        </Form.Group>
                      ))
                    : t(translationConstants.NO_COMMON_SPORT)}
                </div>
                {errors.sportData?.sports && (
                  <p className="errorText">
                    {t(translationConstants.ATLEAST_ONE_SPORT)}
                  </p>
                )}
              </Col>

              <Col md={12}>
                <Form.Label className="Form-labels">
                  {`${t(translationConstants.UPLOAD_FOLLOWING_DOCUMENTS)}:*`}
                </Form.Label>
                <Row>
                  <Col md={4}>
                    <div className="pt-3">
                      {t(translationConstants.SAFETY_PLAN_DOCUMENT)}:
                      <img
                        alt="info-icon"
                        src={IMG_Info}
                        className="info-icon d-none"
                        onClick={() => window.open(sample)}
                      />
                    </div>
                  </Col>
                  <Col md={8}>
                    <div className="filterUploader">
                      <FileUploader
                        sm
                        single
                        disabled={disabled || props.isCenterSpotUpdate}
                        defaultValue={props.modalData?.safetyDocuments}
                        {...register("sportData.safetyDocuments", {
                          required: t(translationConstants.DOCUMENT_REQUIRED),
                        })}
                        onFileUpload={(file) =>
                          handleFileUpload(file, "sportData.safetyDocuments")
                        }
                        setValidationErrorMessage={setSafetyDocsMessage}
                        isError={
                          errors.sportData?.safetyDocuments || safetyDocsMessage
                        }
                        isAllowPreview={
                          props.currentAction === globalGridActions.VIEW ||
                          props.currentAction === globalGridActions.EDIT
                        }
                      />
                      {errors.sportData?.safetyDocuments &&
                        !safetyDocsMessage && (
                          <p className="errorText">
                            {errors.sportData.safetyDocuments.message}
                          </p>
                        )}
                      {safetyDocsMessage && (
                        <p className="errorText">{safetyDocsMessage}</p>
                      )}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="pt-3">
                      {t(translationConstants.OPERATIONAL_PLAN_DOCUMENT)}:
                      <img
                        alt="info-icon"
                        src={IMG_Info}
                        className="info-icon d-none"
                        onClick={() => window.open(sample)}
                      />
                    </div>
                  </Col>
                  <Col md={8}>
                    <div className="filterUploader">
                      <FileUploader
                        sm
                        single
                        disabled={disabled || props.isCenterSpotUpdate}
                        defaultValue={props.modalData?.operationalDocuments}
                        {...register("sportData.operationalDocuments", {
                          required: t(translationConstants.DOCUMENT_REQUIRED),
                        })}
                        onFileUpload={(file) =>
                          handleFileUpload(
                            file,
                            "sportData.operationalDocuments"
                          )
                        }
                        setValidationErrorMessage={setOperationalDocsMessage}
                        isError={
                          errors.sportData?.operationalDocuments ||
                          operationalDocsMessage
                        }
                        isAllowPreview={
                          props.currentAction === globalGridActions.VIEW ||
                          props.currentAction === globalGridActions.EDIT
                        }
                      />
                      {errors.sportData?.operationalDocuments && (
                        <p className="errorText">
                          {errors.sportData.operationalDocuments.message}
                        </p>
                      )}
                      {operationalDocsMessage && (
                        <p className="errorText">{operationalDocsMessage}</p>
                      )}
                    </div>
                  </Col>
                </Row>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Label className="Form-labels">
                  {`${t(translationConstants.AVAILABLE_SERVICES)}:*`}
                </Form.Label>
                <div className="d-flex flex-wrap justify-content-between col-md-12 px-0">
                  <div className="row m-0 w-100">
                    <div className="col-12 col-md-6 col-lg-3 px-0">
                      {" "}
                      <Form.Group
                        className="d-flex flex-column justify-content-space-between"
                        controlId={translationConstants.BEGINNER_LESSONS}
                      >
                        <Form.Check
                          className="offer"
                          type="checkbox"
                          disabled={disabled}
                          value={availableServicesConstants.BEGINNER_LESSONS}
                          label={
                            <p>{t(translationConstants.BEGINNER_LESSONS)} </p>
                          }
                          {...register("sportData.services", {
                            validate: atLeastOneOptionChecked,
                          })}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3 px-0">
                      {" "}
                      <Form.Group
                        className="d-flex flex-column justify-content-space-between "
                        controlId={translationConstants.ADVANCED_LESSONS}
                      >
                        <Form.Check
                          className="offer"
                          type="checkbox"
                          disabled={disabled}
                          value={availableServicesConstants.ADVANCED_LESSONS}
                          label={
                            <p>{t(translationConstants.ADVANCED_LESSONS)} </p>
                          }
                          {...register("sportData.services", {
                            validate: atLeastOneOptionChecked,
                          })}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3 px-0">
                      <Form.Group
                        controlId={translationConstants.EQUIPMENT_RENTALS}
                        className="d-flex flex-column justify-content-space-between "
                      >
                        <Form.Check
                          className="offer"
                          type="checkbox"
                          disabled={disabled}
                          value={availableServicesConstants.EQUIPMENT_RENTALS}
                          label={
                            <p>{t(translationConstants.EQUIPMENT_RENTALS)} </p>
                          }
                          {...register("sportData.services", {
                            validate: atLeastOneOptionChecked,
                          })}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3 px-0">
                      <Form.Group
                        controlId={translationConstants.RESCUE}
                        className="d-flex flex-column justify-content-space-between "
                      >
                        <Form.Check
                          className="offer"
                          type="checkbox"
                          disabled={disabled}
                          value={availableServicesConstants.RESCUE}
                          label={<p>{t(translationConstants.RESCUE)} </p>}
                          {...register("sportData.services", {
                            validate: atLeastOneOptionChecked,
                          })}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
                {errors.sportData?.services && (
                  <p className="errorText">
                    {t(translationConstants.ATLEAST_ONE_SERVICE)}
                  </p>
                )}
              </Col>

              {props.currentAction !== globalGridActions.VIEW && (
                <Col md={12} className="d-flex justify-content-center mt-2">
                  <Button
                    type="submit"
                    disabled={disabled}
                    className="UpdateUserView-button mx-0"
                  >
                    {props.isCenterSpotUpdate
                      ? t(translationConstants.Update)
                      : t(translationConstants.submit)}
                  </Button>
                </Col>
              )}

              {isAdmin && props?.modalData?.status === 2 && (
                <div className="d-flex flex-wrap align-items-center justify-content-center mt-4 gap-3 gap-sm-0">
                  <Button
                    className="approve-button mx-3"
                    type="button"
                    onClick={() => {
                      props.handleApproveCenterSpot(props.modalData);
                    }}
                  >
                    <>
                      <img
                        src={tickIcon}
                        alt=""
                        className="pe-1"
                        name="approved"
                        height={18}
                      />
                      {t(translationConstants.Approve)}
                    </>
                  </Button>
                  <button
                    className="reject-button mx-3"
                    type="button"
                    onClick={() => {
                      props.openRejectSpotModal();
                    }}
                  >
                    <img
                      src={cancelIcon}
                      name="rejected"
                      height={18}
                      alt=""
                      className="pe-1"
                    />
                    {t(translationConstants.Reject)}
                  </button>
                </div>
              )}
            </Row>
          </Container>
        </Form>
      </FormProvider>

      <CustomModal
        size="lg"
        scrollable
        show={privateModal}
        setShow={onClosePrivateModal}
        customClass="custom-modal"
      >
        <PrivateSpotModal
          data={
            !props.isCenterUser
              ? getValues("spotInfo") || props.modalData?.spotInfo
              : getValues(props.modalData?.spotInfo || "spotInfo")
          }
          setShow={onClosePrivateModal}
          onSubmit={onSubmitPrivateSpotModal}
        />
      </CustomModal>
    </>
  );
};

export default AddLocationModal;
